import { PdfTemplateDocumentType } from '../models/entity/pdf-template.entity';
import type { PdfTemplateStatementTableSource } from '../models/interfaces/pdf-template-definition';

/**
 * PD-14688 — the variable, line-item-column, and totals-row catalogs that
 * drive the PDF template builder for each document type.
 *
 * These catalogs are the single contract between the builder UI (variable
 * picker, column picker), the server-side context builders (which must
 * populate every path listed here), and the renderer. Adding a variable
 * means adding it HERE and populating it in the document type's context
 * builder — the builder UI and preview pick it up automatically.
 *
 * Paths are dotted and resolve through the shared template-utils
 * interpolation ({{client.customerId}} etc.). Sample values feed the
 * builder's live preview so a template reads like a real document while
 * being designed.
 */

/**
 * Date formats a {{token|format}} suffix may request, in the order the
 * builder's variable picker offers them. 'short' is the default a bare
 * token renders with, so the picker inserts it without a suffix. The
 * names map 1:1 onto formatDateSimple's styles.
 */
export const PDF_TEMPLATE_DATE_FORMATS = [
	'short',
	'mdy',
	'medium',
	'long',
	'iso',
] as const;
export type PdfTemplateDateFormat = (typeof PDF_TEMPLATE_DATE_FORMATS)[number];

export interface PdfTemplateVariableDefinitionModel {
	/** Dotted path used inside {{tokens}} — e.g. 'invoice.number'. */
	path: string;
	label: string;
	/** Grouping header in the builder's variable picker. */
	category: string;
	sampleValue: string;
	/**
	 * Marks variables whose context value is a tagged raw date (see
	 * PdfTemplateDateValueModel). The picker expands these into one entry
	 * per PDF_TEMPLATE_DATE_FORMATS format, and the context builders must
	 * emit them via pdfTemplateDateValue() rather than pre-formatting.
	 */
	kind?: 'date';
}

export interface PdfTemplateLineItemColumnDefinitionModel {
	/** Column key — matches the keys of each rendered line-item row. */
	key: string;
	label: string;
	sampleValues: Array<string>;
	align?: 'left' | 'center' | 'right';
	/** Default width hint (percent of the table) used when first added. */
	defaultWidthPct?: number;
}

export interface PdfTemplateTotalsRowDefinitionModel {
	/** Row key — matches the keys of the rendered totals map. */
	key: string;
	label: string;
	sampleValue: string;
	/** Rows that are usually noise at zero default to hidden-when-zero. */
	defaultHideWhenZero?: boolean;
}

export const INVOICE_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	[
		// ── Document ────────────────────────────────────────────────────
		{
			path: 'invoice.number',
			label: 'Invoice Number',
			category: 'Invoice',
			sampleValue: 'INV0378415',
		},
		{
			path: 'invoice.date',
			label: 'Invoice Date',
			category: 'Invoice',
			sampleValue: '6/17/2026',
			kind: 'date',
		},
		{
			path: 'invoice.dueDate',
			label: 'Due Date',
			category: 'Invoice',
			sampleValue: '7/10/2026',
			kind: 'date',
		},
		{
			path: 'invoice.paymentTerm',
			label: 'Payment Terms',
			category: 'Invoice',
			sampleValue: 'EOM+10',
		},
		{
			path: 'invoice.status',
			label: 'Invoice Status',
			category: 'Invoice',
			sampleValue: 'Balance Due',
		},
		{
			path: 'invoice.poNumbers',
			label: 'Customer PO #(s)',
			category: 'Invoice',
			sampleValue: 'PO-8543330',
		},
		{
			path: 'invoice.salesOrderNumber',
			label: 'Sales Order #',
			category: 'Invoice',
			sampleValue: 'SO646337596',
		},
		{
			path: 'invoice.orderDate',
			label: 'Order Date',
			category: 'Invoice',
			sampleValue: '6/15/2026',
			kind: 'date',
		},
		{
			path: 'invoice.memo',
			label: 'Memo',
			category: 'Invoice',
			sampleValue: 'Thank you for your business.',
		},
		{
			path: 'invoice.pageTitle',
			label: 'Document Title',
			category: 'Invoice',
			sampleValue: 'Invoice',
		},
		// ── Amounts (also available as totals-box rows) ─────────────────
		{
			path: 'invoice.subtotal',
			label: 'Subtotal',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'invoice.discount',
			label: 'Discount',
			category: 'Amounts',
			sampleValue: '-$12.50',
		},
		{
			path: 'invoice.tax',
			label: 'Tax',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'invoice.shipping',
			label: 'Shipping Cost',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'invoice.processingFee',
			label: 'Processing Fee',
			category: 'Amounts',
			sampleValue: '$6.76',
		},
		{
			path: 'invoice.total',
			label: 'Total',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'invoice.amountPaid',
			label: 'Amount Paid',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'invoice.balanceDue',
			label: 'Balance Due',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		// ── Customer ────────────────────────────────────────────────────
		{
			path: 'client.name',
			label: 'Customer Name',
			category: 'Customer',
			sampleValue: 'Maks Pharma And Diagnostics Inc',
		},
		{
			path: 'client.customerId',
			label: 'Customer ID',
			category: 'Customer',
			sampleValue: '8595',
		},
		{
			path: 'client.email',
			label: 'Customer Email',
			category: 'Customer',
			sampleValue: 'orders@makspharma.com',
		},
		{
			path: 'client.phone',
			label: 'Customer Phone',
			category: 'Customer',
			sampleValue: '(516) 555-0184',
		},
		{
			path: 'client.licenseNumber',
			label: 'Customer License #',
			category: 'Customer',
			sampleValue: '031584',
		},
		{
			path: 'client.deaNumber',
			label: 'Customer DEA #',
			category: 'Customer',
			sampleValue: 'RM0504212',
		},
		{
			path: 'client.salesRep',
			label: 'Sales Rep',
			category: 'Customer',
			sampleValue: 'Dean Sages',
		},
		{
			path: 'client.autoPayMethod',
			label: 'Auto Pay Method',
			category: 'Customer',
			sampleValue: 'Visa ending 4242',
		},
		{
			path: 'client.creditBalance',
			label: 'Credit Balance',
			category: 'Customer',
			sampleValue: '$0.00',
		},
		// ── Shipping ────────────────────────────────────────────────────
		{
			path: 'shipping.method',
			label: 'Shipping Method',
			category: 'Shipping',
			sampleValue: 'UPS Next Day Air',
		},
		{
			path: 'shipping.trackingNumber',
			label: 'Tracking Number',
			category: 'Shipping',
			sampleValue: '1ZF29Y960198627334',
		},
		{
			path: 'shipping.trackingUrl',
			label: 'Tracking URL',
			category: 'Shipping',
			sampleValue: 'https://www.ups.com/track?tracknum=1ZF29Y960198627334',
		},
		// ── Company (the issuing company / tenant) ──────────────────────
		{
			path: 'company.name',
			label: 'Company Name',
			category: 'Company',
			sampleValue: 'Pharmsource, LLC',
		},
		{
			path: 'company.email',
			label: 'Company Email',
			category: 'Company',
			sampleValue: 'ar@pharmsource.com',
		},
	];

export const INVOICE_PDF_TEMPLATE_LINE_ITEM_COLUMNS: Array<PdfTemplateLineItemColumnDefinitionModel> =
	[
		{
			key: 'itemNumber',
			label: 'SKU',
			sampleValues: ['201222', '201471', '205452'],
			defaultWidthPct: 10,
		},
		{
			key: 'description',
			label: 'Description',
			sampleValues: [
				'PANTOPRAZOLE SODIUM DR 40MG TABLET (PROTONIX)',
				'TERCONAZOLE VAGINAL 0.4% CREAM (TERAZOL)',
				'METFORMIN HCL TABLET 500MG (GLUCOPHAGE)',
			],
			defaultWidthPct: 34,
		},
		{
			key: 'size',
			label: 'Size',
			sampleValues: ['1000TB', '45GM', '1X1000EA'],
			defaultWidthPct: 10,
		},
		{
			key: 'unitOfMeasure',
			label: 'UOM',
			sampleValues: ['EA', 'EA', 'EA'],
			defaultWidthPct: 8,
		},
		{
			key: 'ndc',
			label: 'NDC',
			sampleValues: ['31722-0713-10', '51672-1304-06', '69367-0381-10'],
			defaultWidthPct: 14,
		},
		{
			// Lot number(s) allocated to the line (PD-15838). Only ERPs that report
			// lot allocations fill invoice_detail.lotNumber, so the column is offered
			// but not part of any shipped template; blank lines render empty cells.
			key: 'lotNumber',
			label: 'Lot',
			sampleValues: ['AE06421', '5PK12A', '26N179'],
			defaultWidthPct: 12,
		},
		{
			key: 'orderedQuantity',
			label: 'Ordered Qty',
			sampleValues: ['4', '2', '6'],
			align: 'right',
			defaultWidthPct: 8,
		},
		{
			key: 'quantity',
			label: 'Quantity',
			sampleValues: ['4', '2', '6'],
			align: 'right',
			defaultWidthPct: 8,
		},
		{
			key: 'price',
			label: 'Price',
			sampleValues: ['$21.50', '$18.50', '$7.50'],
			align: 'right',
			defaultWidthPct: 8,
		},
		{
			key: 'total',
			label: 'Total',
			sampleValues: ['$86.00', '$37.00', '$45.00'],
			align: 'right',
			defaultWidthPct: 8,
		},
	];

export const INVOICE_PDF_TEMPLATE_TOTALS_ROWS: Array<PdfTemplateTotalsRowDefinitionModel> =
	[
		{ key: 'subtotal', label: 'Subtotal', sampleValue: '$225.40' },
		{
			key: 'discount',
			label: 'Discount',
			sampleValue: '-$12.50',
			defaultHideWhenZero: true,
		},
		{ key: 'tax', label: 'Tax Total', sampleValue: '$0.00' },
		{
			key: 'shipping',
			label: 'Shipping Cost',
			sampleValue: '$0.00',
		},
		{
			key: 'processingFee',
			label: 'Processing Fee',
			sampleValue: '$6.76',
			defaultHideWhenZero: true,
		},
		{ key: 'total', label: 'Total', sampleValue: '$225.40' },
		{
			key: 'amountPaid',
			label: 'Amount Paid',
			sampleValue: '$0.00',
			defaultHideWhenZero: true,
		},
		{
			key: 'balanceDue',
			label: 'Balance Due',
			sampleValue: '$225.40',
		},
	];

/**
 * The customer/company variable groups are identical across document
 * types — built once so a label fix applies everywhere.
 */
const CUSTOMER_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	INVOICE_PDF_TEMPLATE_VARIABLES.filter(
		(variable) => variable.category === 'Customer',
	);

const COMPANY_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	INVOICE_PDF_TEMPLATE_VARIABLES.filter(
		(variable) => variable.category === 'Company',
	);

export const ORDER_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	[
		// ── Document ────────────────────────────────────────────────────
		{
			path: 'order.number',
			label: 'Order Number',
			category: 'Order',
			sampleValue: '1268097',
		},
		{
			path: 'order.date',
			label: 'Order Date',
			category: 'Order',
			sampleValue: '6/15/2026',
			kind: 'date',
		},
		{
			path: 'order.customerPo',
			label: 'Customer PO #',
			category: 'Order',
			sampleValue: 'PO-8543330',
		},
		{
			path: 'order.pageTitle',
			label: 'Document Title',
			category: 'Order',
			sampleValue: 'Order',
		},
		// ── Amounts (also available as totals-box rows) ─────────────────
		{
			path: 'order.subtotal',
			label: 'Subtotal',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'order.discount',
			label: 'Discount',
			category: 'Amounts',
			sampleValue: '-$12.50',
		},
		{
			path: 'order.tax',
			label: 'Tax',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'order.shipping',
			label: 'Shipping Cost',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'order.processingFee',
			label: 'Processing Fee',
			category: 'Amounts',
			sampleValue: '$6.76',
		},
		{
			path: 'order.total',
			label: 'Total',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		...CUSTOMER_PDF_TEMPLATE_VARIABLES,
		...COMPANY_PDF_TEMPLATE_VARIABLES,
	];

export const QUOTE_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	[
		// ── Document ────────────────────────────────────────────────────
		{
			path: 'quote.number',
			label: 'Quote Number',
			category: 'Quote',
			sampleValue: 'Q-1268097',
		},
		{
			path: 'quote.date',
			label: 'Quote Date',
			category: 'Quote',
			sampleValue: '6/15/2026',
			kind: 'date',
		},
		{
			path: 'quote.customerPo',
			label: 'Customer PO #',
			category: 'Quote',
			sampleValue: 'PO-8543330',
		},
		{
			path: 'quote.pageTitle',
			label: 'Document Title',
			category: 'Quote',
			sampleValue: 'Quote',
		},
		// ── Amounts (also available as totals-box rows) ─────────────────
		{
			path: 'quote.subtotal',
			label: 'Subtotal',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		{
			path: 'quote.discount',
			label: 'Discount',
			category: 'Amounts',
			sampleValue: '-$12.50',
		},
		{
			path: 'quote.tax',
			label: 'Tax',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'quote.shipping',
			label: 'Shipping Cost',
			category: 'Amounts',
			sampleValue: '$0.00',
		},
		{
			path: 'quote.total',
			label: 'Total',
			category: 'Amounts',
			sampleValue: '$225.40',
		},
		...CUSTOMER_PDF_TEMPLATE_VARIABLES,
		...COMPANY_PDF_TEMPLATE_VARIABLES,
	];

/**
 * Order/quote line tables show the ORDERED quantity (there is no shipped
 * split before fulfillment), so the ordered-vs-shipped pair collapses to
 * the single Quantity column. Lots are allocated at fulfillment, so an
 * order or quote line has none to print either.
 */
export const ORDER_PDF_TEMPLATE_LINE_ITEM_COLUMNS: Array<PdfTemplateLineItemColumnDefinitionModel> =
	INVOICE_PDF_TEMPLATE_LINE_ITEM_COLUMNS.filter(
		(column) => column.key !== 'orderedQuantity' && column.key !== 'lotNumber',
	);

export const ORDER_PDF_TEMPLATE_TOTALS_ROWS: Array<PdfTemplateTotalsRowDefinitionModel> =
	[
		{ key: 'subtotal', label: 'Subtotal', sampleValue: '$225.40' },
		{
			key: 'discount',
			label: 'Discount',
			sampleValue: '-$12.50',
			defaultHideWhenZero: true,
		},
		{ key: 'tax', label: 'Tax Total', sampleValue: '$0.00' },
		{
			key: 'shipping',
			label: 'Shipping Cost',
			sampleValue: '$0.00',
			defaultHideWhenZero: true,
		},
		{
			key: 'processingFee',
			label: 'Processing Fee',
			sampleValue: '$6.76',
			defaultHideWhenZero: true,
		},
		{ key: 'total', label: 'Total', sampleValue: '$225.40' },
	];

export const QUOTE_PDF_TEMPLATE_TOTALS_ROWS: Array<PdfTemplateTotalsRowDefinitionModel> =
	ORDER_PDF_TEMPLATE_TOTALS_ROWS.filter((row) => row.key !== 'processingFee');

// ─── Statements (PD-16751) ──────────────────────────────────────────
//
// A statement is not a document with line items: it is an account summary
// for a period. Its variables describe the statement and its balances, its
// totals rows are the period reconciliation, and its lists (the period
// activity, unapplied payments, open items, …) are placed with the statement
// widgets, whose column catalogs follow. Every value comes from the stored
// statement build (CustomerStatementModel) — the template only lays it out.

export const STATEMENT_PDF_TEMPLATE_VARIABLES: Array<PdfTemplateVariableDefinitionModel> =
	[
		// ── Statement ───────────────────────────────────────────────────
		{
			path: 'statement.number',
			label: 'Statement Number',
			category: 'Statement',
			sampleValue: 'ST-747',
		},
		{
			path: 'statement.date',
			label: 'Statement Date',
			category: 'Statement',
			sampleValue: '9/30/2026',
			kind: 'date',
		},
		{
			path: 'statement.periodStart',
			label: 'Period Start',
			category: 'Statement',
			sampleValue: '9/1/2026',
			kind: 'date',
		},
		{
			path: 'statement.periodEnd',
			label: 'Period End',
			category: 'Statement',
			sampleValue: '9/30/2026',
			kind: 'date',
		},
		{
			path: 'statement.paymentTerms',
			label: 'Terms',
			category: 'Statement',
			sampleValue: 'Net 30',
		},
		{
			path: 'statement.currency',
			label: 'Currency',
			category: 'Statement',
			sampleValue: 'USD',
		},
		{
			path: 'statement.pageTitle',
			label: 'Document Title',
			category: 'Statement',
			sampleValue: 'Statement',
		},
		// ── Balances ────────────────────────────────────────────────────
		{
			path: 'statement.totalBalance',
			label: 'Total Balance Due',
			category: 'Balances',
			sampleValue: '$1,767.30',
		},
		{
			path: 'statement.openingBalance',
			label: 'Opening Balance',
			category: 'Balances',
			sampleValue: '$1,215.30',
		},
		{
			path: 'statement.invoicesAndCharges',
			label: 'Invoices & Charges',
			category: 'Balances',
			sampleValue: '$2,767.30',
		},
		{
			path: 'statement.paymentsAndCredits',
			label: 'Payments & Credits',
			category: 'Balances',
			sampleValue: '-$2,215.30',
		},
		{
			path: 'statement.adjustments',
			label: 'Adjustments',
			category: 'Balances',
			sampleValue: '$0.00',
		},
		{
			path: 'statement.closingBalance',
			label: 'Closing Balance',
			category: 'Balances',
			sampleValue: '$1,767.30',
		},
		{
			path: 'statement.availableCredit',
			label: 'Available Credit',
			category: 'Balances',
			sampleValue: '$290.50',
		},
		{
			path: 'statement.availableCreditText',
			label: 'Available Credit Note',
			category: 'Balances',
			sampleValue:
				'Available credit: $290.50 (credit memos $40.50, account funds $250.00), not yet applied to an invoice',
		},
		// ── Aging ───────────────────────────────────────────────────────
		{
			path: 'statement.aging.current',
			label: 'Aging: Current',
			category: 'Aging',
			sampleValue: '$1,127.30',
		},
		{
			path: 'statement.aging.days1to30',
			label: 'Aging: 1-30 Days',
			category: 'Aging',
			sampleValue: '$640.00',
		},
		{
			path: 'statement.aging.days31to60',
			label: 'Aging: 31-60 Days',
			category: 'Aging',
			sampleValue: '$0.00',
		},
		{
			path: 'statement.aging.days61to90',
			label: 'Aging: 61-90 Days',
			category: 'Aging',
			sampleValue: '$0.00',
		},
		{
			path: 'statement.aging.days90plus',
			label: 'Aging: Over 90 Days',
			category: 'Aging',
			sampleValue: '$0.00',
		},
		{
			path: 'statement.aging.total',
			label: 'Aging: Total Due',
			category: 'Aging',
			sampleValue: '$1,767.30',
		},
		// ── Customer ────────────────────────────────────────────────────
		{
			path: 'client.name',
			label: 'Customer Name',
			category: 'Customer',
			sampleValue: 'Mandarin House',
		},
		{
			path: 'client.customerId',
			label: 'Customer ID',
			category: 'Customer',
			sampleValue: '10482',
		},
		{
			path: 'client.email',
			label: 'Customer Email',
			category: 'Customer',
			sampleValue: 'office@mandarinhouse.com',
		},
		{
			path: 'client.phone',
			label: 'Customer Phone',
			category: 'Customer',
			sampleValue: '(206) 555-0142',
		},
		// ── Remit To (where the customer sends payment) ─────────────────
		{
			path: 'remitTo.name',
			label: 'Remit To Name',
			category: 'Remit To',
			sampleValue: 'Capital Trading',
		},
		{
			path: 'remitTo.phone',
			label: 'Remit To Phone',
			category: 'Remit To',
			sampleValue: '(206) 555-0100',
		},
		{
			path: 'remitTo.fax',
			label: 'Remit To Fax',
			category: 'Remit To',
			sampleValue: '(206) 555-0101',
		},
		...COMPANY_PDF_TEMPLATE_VARIABLES,
	];

/**
 * The statement's totals-box rows: the period reconciliation (opening +
 * invoices − payments + adjustments = closing), the balance due and the
 * available credit beside it. Payments read as a subtraction, as on the
 * bundled statement.
 */
export const STATEMENT_PDF_TEMPLATE_TOTALS_ROWS: Array<PdfTemplateTotalsRowDefinitionModel> =
	[
		{ key: 'openingBalance', label: 'Opening Balance', sampleValue: '$1,215.30' },
		{
			key: 'invoicesAndCharges',
			label: 'Invoices & Charges',
			sampleValue: '$2,767.30',
		},
		{
			key: 'paymentsAndCredits',
			label: 'Payments & Credits',
			sampleValue: '-$2,215.30',
		},
		{
			key: 'adjustments',
			label: 'Adjustments',
			sampleValue: '$0.00',
			defaultHideWhenZero: true,
		},
		{ key: 'closingBalance', label: 'Closing Balance', sampleValue: '$1,767.30' },
		{
			key: 'totalBalance',
			label: 'Total Balance Due',
			sampleValue: '$1,767.30',
		},
		{
			key: 'availableCredit',
			label: 'Available Credit',
			sampleValue: '$290.50',
			defaultHideWhenZero: true,
		},
	];

/**
 * Columns of the statement activity ledger (the statementActivity widget),
 * laid out like HF's statement: every money cell is filled ($0.00 where a
 * row has nothing), and money received is negative. Invoice rows fill Invoice
 * Amount and Balance (what is still open on the invoice at the period end).
 * A payment row describes the payment under Number ("Payment - Check
 * (11402)"), shows what it paid on invoices under Payment and what it put on
 * the account under Applied on Account, and lists the invoices it paid under
 * Description. Adjustment rows carry their change in Invoice Amount.
 */
export const STATEMENT_PDF_TEMPLATE_ACTIVITY_COLUMNS: Array<PdfTemplateLineItemColumnDefinitionModel> =
	[
		{
			key: 'date',
			label: 'Date',
			sampleValues: ['9/3/2026', '9/12/2026', '9/21/2026'],
			// Narrow: the month heading spans the whole row, not this column.
			defaultWidthPct: 9,
		},
		{
			key: 'number',
			label: 'Number',
			// A payment row describes the payment here, as HF's statement does.
			sampleValues: ['INV-230118', 'Payment - Check (11402)', 'CAP-RMA 11074'],
			// Wide enough for "Payment - Credit Card x4444 (BF4798L9BX9GW7V5)".
			defaultWidthPct: 30,
		},
		{
			key: 'type',
			label: 'Type',
			sampleValues: ['Invoice', 'Payment', 'Credit Memo'],
			defaultWidthPct: 11,
		},
		{
			key: 'description',
			label: 'Description',
			// The invoices a payment paid, or what an adjustment is.
			sampleValues: ['', 'INV-229874', ''],
			defaultWidthPct: 14,
		},
		{
			key: 'dueDate',
			label: 'Due Date',
			sampleValues: ['10/3/2026', '', '9/21/2026'],
			defaultWidthPct: 11,
		},
		{
			key: 'appliedOnAccount',
			label: 'Applied on Account',
			// The part of a payment put on the account rather than an invoice.
			sampleValues: ['$0.00', '$0.00', '$0.00'],
			align: 'right',
			// Wide enough that "Applied on Account" stays on one line.
			defaultWidthPct: 15,
		},
		{
			key: 'invoiceAmount',
			label: 'Invoice Amount',
			sampleValues: ['$1,127.30', '$0.00', '-$40.50'],
			align: 'right',
			defaultWidthPct: 12,
		},
		{
			key: 'payment',
			label: 'Payment',
			sampleValues: ['$0.00', '-$1,500.00', '$0.00'],
			align: 'right',
			defaultWidthPct: 12,
		},
		{
			key: 'balance',
			label: 'Balance',
			sampleValues: ['$1,127.30', '$0.00', '-$40.50'],
			align: 'right',
			defaultWidthPct: 12,
		},
	];

/** What each statement list is, for the statementTable widget's picker. */
export interface PdfTemplateStatementTableSourceDefinitionModel {
	source: PdfTemplateStatementTableSource;
	label: string;
	description: string;
	/** Caption a freshly placed table starts with. */
	defaultTitle: string;
	/** Columns a freshly placed table starts with, in order. */
	defaultColumns: Array<string>;
}

export const STATEMENT_PDF_TEMPLATE_TABLE_SOURCES: Array<PdfTemplateStatementTableSourceDefinitionModel> =
	[
		{
			source: 'unappliedPayments',
			label: 'Unapplied payments',
			description:
				'Payments in the period that left money on the account instead of paying an invoice',
			defaultTitle: 'Unapplied Payments',
			defaultColumns: ['date', 'type', 'reference', 'description', 'amount'],
		},
		{
			source: 'invoices',
			label: 'Invoices this period',
			description: 'Invoices and credit memos dated in the period',
			defaultTitle: 'Invoices this period',
			defaultColumns: [
				'number',
				'type',
				'date',
				'dueDate',
				'originalAmount',
				'balance',
			],
		},
		{
			source: 'payments',
			label: 'Payments & credits this period',
			description: 'Payments, refunds and account-credit lines in the period',
			defaultTitle: 'Payments & credits this period',
			defaultColumns: [
				'date',
				'type',
				'method',
				'reference',
				'amount',
				'appliedTo',
			],
		},
		{
			source: 'openItems',
			label: 'Open items',
			description: 'Invoices still open as of the statement date',
			defaultTitle: 'Open Items',
			defaultColumns: [
				'number',
				'date',
				'dueDate',
				'originalTotal',
				'applied',
				'outstanding',
			],
		},
		{
			source: 'openCredits',
			label: 'Available credit memos',
			description: 'Credit memos with credit not yet applied',
			defaultTitle: 'Available Credit',
			defaultColumns: ['number', 'date', 'originalAmount', 'available'],
		},
		{
			source: 'adjustments',
			label: 'Adjustments',
			description:
				'Invoice balance changes that no listed payment explains, e.g. a return applied in the ERP',
			defaultTitle: 'Adjustments',
			defaultColumns: [
				'number',
				'date',
				'description',
				'balanceBefore',
				'balanceAfter',
				'amount',
			],
		},
	];

const STATEMENT_DATE_COLUMN: PdfTemplateLineItemColumnDefinitionModel = {
	key: 'date',
	label: 'Date',
	sampleValues: ['9/12/2026'],
	defaultWidthPct: 12,
};

export const STATEMENT_PDF_TEMPLATE_TABLE_COLUMNS: Record<
	PdfTemplateStatementTableSource,
	Array<PdfTemplateLineItemColumnDefinitionModel>
> = {
	invoices: [
		{ key: 'number', label: 'Number', sampleValues: ['INV-230118'] },
		{ key: 'type', label: 'Type', sampleValues: ['Invoice'] },
		STATEMENT_DATE_COLUMN,
		{ key: 'dueDate', label: 'Due Date', sampleValues: ['10/3/2026'] },
		{ key: 'note', label: 'Note', sampleValues: [''] },
		{
			key: 'originalAmount',
			label: 'Original Amount',
			sampleValues: ['$1,127.30'],
			align: 'right',
		},
		{
			key: 'balance',
			label: 'Balance at Period End',
			sampleValues: ['$1,127.30'],
			align: 'right',
		},
	],
	payments: [
		STATEMENT_DATE_COLUMN,
		{ key: 'type', label: 'Type', sampleValues: ['Payment'] },
		{ key: 'method', label: 'Method', sampleValues: ['Check'] },
		{ key: 'reference', label: 'Reference', sampleValues: ['11402'] },
		{
			key: 'amount',
			label: 'Amount',
			sampleValues: ['$1,500.00'],
			align: 'right',
		},
		{ key: 'fee', label: 'Fee', sampleValues: [''], align: 'right' },
		{
			key: 'appliedTo',
			label: 'Applied To',
			sampleValues: ['INV-229874: $1,500.00'],
		},
	],
	unappliedPayments: [
		STATEMENT_DATE_COLUMN,
		{ key: 'type', label: 'Type', sampleValues: ['Payment'] },
		{
			key: 'reference',
			label: 'Payment Reference',
			sampleValues: ['11431'],
		},
		{ key: 'description', label: 'Description', sampleValues: ['Check'] },
		{
			key: 'amount',
			label: 'Payment Amount',
			sampleValues: ['$250.00'],
			align: 'right',
		},
		{
			key: 'unappliedAmount',
			label: 'Unapplied Amount',
			sampleValues: ['$250.00'],
			align: 'right',
		},
	],
	openItems: [
		{ key: 'number', label: 'Invoice', sampleValues: ['INV-230118'] },
		STATEMENT_DATE_COLUMN,
		{ key: 'dueDate', label: 'Due', sampleValues: ['10/3/2026'] },
		{ key: 'billedFor', label: 'For', sampleValues: [''] },
		{
			key: 'originalTotal',
			label: 'Original',
			sampleValues: ['$1,127.30'],
			align: 'right',
		},
		{
			key: 'applied',
			label: 'Applied',
			sampleValues: ['$0.00'],
			align: 'right',
		},
		{
			key: 'outstanding',
			label: 'Outstanding',
			sampleValues: ['$1,127.30'],
			align: 'right',
		},
	],
	openCredits: [
		{ key: 'number', label: 'Credit Memo', sampleValues: ['CAP-RMA 11074'] },
		STATEMENT_DATE_COLUMN,
		{ key: 'billedFor', label: 'For', sampleValues: [''] },
		{
			key: 'originalAmount',
			label: 'Original',
			sampleValues: ['$40.50'],
			align: 'right',
		},
		{
			key: 'available',
			label: 'Available',
			sampleValues: ['$40.50'],
			align: 'right',
		},
	],
	adjustments: [
		{ key: 'number', label: 'Invoice', sampleValues: ['INV-229874'] },
		STATEMENT_DATE_COLUMN,
		{
			key: 'description',
			label: 'Description',
			sampleValues: ['Balance adjustment'],
		},
		{
			key: 'balanceBefore',
			label: 'Before',
			sampleValues: ['$88.00'],
			align: 'right',
		},
		{
			key: 'balanceAfter',
			label: 'After',
			sampleValues: ['$47.50'],
			align: 'right',
		},
		{
			key: 'amount',
			label: 'Change',
			sampleValues: ['-$40.50'],
			align: 'right',
		},
	],
};

/**
 * Catalog lookups keyed by document type. Statements have no line items;
 * their lists use the statement column catalogs above.
 */
export const PDF_TEMPLATE_VARIABLES_BY_DOCUMENT_TYPE: Partial<
	Record<PdfTemplateDocumentType, Array<PdfTemplateVariableDefinitionModel>>
> = {
	[PdfTemplateDocumentType.INVOICE]: INVOICE_PDF_TEMPLATE_VARIABLES,
	[PdfTemplateDocumentType.ORDER]: ORDER_PDF_TEMPLATE_VARIABLES,
	[PdfTemplateDocumentType.QUOTE]: QUOTE_PDF_TEMPLATE_VARIABLES,
	[PdfTemplateDocumentType.STATEMENT]: STATEMENT_PDF_TEMPLATE_VARIABLES,
};

export const PDF_TEMPLATE_LINE_ITEM_COLUMNS_BY_DOCUMENT_TYPE: Partial<
	Record<
		PdfTemplateDocumentType,
		Array<PdfTemplateLineItemColumnDefinitionModel>
	>
> = {
	[PdfTemplateDocumentType.INVOICE]: INVOICE_PDF_TEMPLATE_LINE_ITEM_COLUMNS,
	[PdfTemplateDocumentType.ORDER]: ORDER_PDF_TEMPLATE_LINE_ITEM_COLUMNS,
	[PdfTemplateDocumentType.QUOTE]: ORDER_PDF_TEMPLATE_LINE_ITEM_COLUMNS,
};

export const PDF_TEMPLATE_TOTALS_ROWS_BY_DOCUMENT_TYPE: Partial<
	Record<PdfTemplateDocumentType, Array<PdfTemplateTotalsRowDefinitionModel>>
> = {
	[PdfTemplateDocumentType.INVOICE]: INVOICE_PDF_TEMPLATE_TOTALS_ROWS,
	[PdfTemplateDocumentType.ORDER]: ORDER_PDF_TEMPLATE_TOTALS_ROWS,
	[PdfTemplateDocumentType.QUOTE]: QUOTE_PDF_TEMPLATE_TOTALS_ROWS,
	[PdfTemplateDocumentType.STATEMENT]: STATEMENT_PDF_TEMPLATE_TOTALS_ROWS,
};
